<template>
  <form
    class="max-w-lg"
    @submit.prevent="handleSubmit"
  >
    <fieldset class="mb-4">
      <div class="field-list">
        <div class="field sm:w-full pb-2">
          <label
            for="external_account_account_type"
            v-text="'Account Type *'"
          />
          <select
            id="external_account_account_type"
            v-model="form.accountType"
            :class="{ 'error': form.errors.has('accountType') }"
            name="external_account_account_type"
          >
            <option
              disabled
              value=""
              v-text="'Please select one'"
            />
            <option
              v-for="type in accountTypes"
              :key="type"
              :value="type"
              v-text="type.slice(0, 1).toUpperCase() + type.slice(1)"
            />
          </select>
          <div class="field__meta">
            <span
              v-if="form.errors.has('accountType')"
              class="field__meta__help"
              v-text="form.errors.get('accountType')"
            />
          </div>
        </div>
        <div class="field sm:w-full pb-2">
          <label
            for="external_account_account_holder_name"
            v-text="'Account Holder *'"
          />
          <input
            id="external_account_account_holder_name"
            v-model="form.accountHolderName"
            :class="{ 'error': form.errors.has('accountHolderName') }"
            placeholder="John Doe"
            type="text"
            name="external_account_account_holder_name"
          >
          <div class="field__meta">
            <span
              v-if="form.errors.has('accountHolderName')"
              class="field__meta__help"
              v-text="form.errors.get('accountHolderName')"
            />
          </div>
        </div>
        <div class="field sm:w-1/2 pb-2">
          <label
            for="external_account_routing_number"
            v-text="'Routing Number *'"
          />
          <input
            id="external_account_routing_number"
            v-model="form.routingNumber"
            :class="{ 'error': form.errors.has('routingNumber') }"
            placeholder="110000000"
            type="text"
            name="external_account_routing_number"
          >
          <div class="field__meta">
            <span
              v-if="form.errors.has('routingNumber')"
              class="field__meta__help"
              v-text="form.errors.get('routingNumber')"
            />
          </div>
        </div>
        <div class="field sm:w-1/2 pb-2">
          <label
            for="external_account_account_number"
            v-text="'Account Number *'"
          />
          <input
            id="external_account_account_number"
            v-model="form.accountNumber"
            :class="{ 'error': form.errors.has('accountNumber') }"
            placeholder="000123456789"
            type="text"
            name="external_account_account_number"
          >
          <div class="field__meta">
            <span
              v-if="form.errors.has('accountNumber')"
              class="field__meta__help"
              v-text="form.errors.get('accountNumber')"
            />
          </div>
        </div>
        <div class="field sm:w-full pb-2">
          <label
            for="external_account_payout_statement_descriptor"
            v-text="'Payment Descriptor *'"
          />
          <input
            id="external_account_payout_statement_descriptor"
            v-model="form.payoutStatementDescriptor"
            :class="{ 'error': form.errors.has('statementDescriptor') }"
            placeholder="Statement descriptor"
            type="text"
            name="external_account_payout_statement_descriptor"
          >
          <div class="field__meta">
            <span
              v-if="form.errors.has('statementDescriptor')"
              class="field__meta__help"
              v-text="form.errors.get('statementDescriptor')"
            />
          </div>
        </div>
      </div>
    </fieldset>
    <div class="flex mb-4">
      <button
        class="btn btn--sm px-6"
        type="submit"
        :disabled="loading"
        :aria-disabled="loading"
        v-text="'Save'"
      />
      <button
        v-if="stripeAccount.external_accounts.data.length"
        class="btn--grey btn--sm rounded-full px-6 ml-6"
        type="button"
        :disabled="loading"
        :aria-disabled="loading"
        @click="$emit('hideForm')"
        v-text="'Cancel'"
      />
    </div>
  </form>
</template>

<script type="text/babel">
import { mapActions, mapState } from 'vuex'
import Form from 'Base/Resources/assets/js/plugins/Form'
import StripeMixin from '../../mixins/stripeMixin'

export default {
  name: 'BankAccountForm',
  mixins: [
    StripeMixin,
  ],
  data() {
    return {
      cardErrors: {},
      loading: false,
      hasCardErrors: false,
      form: new Form({
        accountType: '',
        accountHolderName: '',
        currency: '',
        routingNumber: '',
        accountNumber: '',
        payoutStatementDescriptor: '',
      }),
      accountTypes: [
        'individual',
        'company',
      ],
    }
  },
  computed: {
    ...mapState({
      stripeAccount: state => state.payments.stripeAccount,
    }),
  },
  mounted() {
    this.form.currency = this.stripeAccount.default_currency
  },
  methods: {
    ...mapActions([
      'paymentsCreateBankAccount',
      'paymentsUpdateBankAccount',
      'paymentsRefreshAccount',
    ]),
    async handleSubmit() {
      this.hasCardErrors = false
      this.cardErrors = []
      this.loading = true

      this.passesValidation()

      const { token, error } = await this.stripe.createToken('bank_account', {
        country: this.stripeAccount.country,
        currency: this.stripeAccount.default_currency,
        routing_number: this.form.routingNumber,
        account_number: this.form.accountNumber,
        account_holder_name: this.form.accountHolderName,
        account_holder_type: this.form.accountType,
      })

      if (error) {
        this.cardErrors = error
        this.hasCardErrors = true
        this.loading = false

        this.$forceUpdate()

        return true
      }

      const action = this.stripeAccount.external_accounts.total_count
        ? this.paymentsUpdateBankAccount
        : this.paymentsCreateBankAccount

      try {
        const bankAccount = await action({
          token: token.id,
          payoutStatementDescriptor: this.form.payoutStatementDescriptor,
        })

        this.paymentsRefreshAccount()

        window.location.reload()
      } catch (errors) {
        this.loading = false

        this.form.errors.set(errors)
      }
    },
    passesValidation() {
      this.form.errors.clear()

      const errors = {}

      if (!this.form.accountType) {
        errors.accountType = 'Account type is required.'
      }

      if (!this.form.accountHolderName) {
        errors.accountHolderName = 'Account holder name is required.'
      }

      if (!this.form.routingNumber) {
        errors.routingNumber = 'Routing number is required.'
      }

      if (!this.form.accountNumber) {
        errors.accountNumber = 'Account number is required.'
      }

      if (!this.form.payoutStatementDescriptor) {
        errors.statementDescriptor = 'Statement descriptors explain charges or payments on bank statements. It should reflect your DBA name, contain at least 5 characters and no numbers or special characters.'
      }

      if (Object.keys(errors).length > 0) {
        this.form.setErrors({
          message: 'The given data was invalid',
          errors: errors,
        })
      }

      return !this.form.errors.any()
    },
  },
}
</script>